import {
  IconButton,
  List,
  ListBody,
  ListCell,
  ListHead,
  ListHeadCell,
  ListRow,
  Loader,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '@plaidev/sour-react';
import type { MouseEvent } from 'react';
import type {} from 'react/jsx-runtime';
import { DomainV2 } from 'troppus-common/domain';
import { importTableDefinitionLoadModeLabel } from './importTableDefinitionLoadModeLabel';

import ImportTableDefinition = DomainV2.Import.ImportTableDefinition;

export type TableDefinitionDeleteTarget = {
  id: ImportTableDefinition.Id;
  displayName: string;
};

type TableDefinitionListProps = {
  rows: ImportTableDefinition.Detail[];
  loading: boolean;
  sourceSystemNameById: Map<string, string>;
  memberNameById: Map<string, string>;
  isSystemAdmin: boolean;
  isProjectAdmin: boolean;
  onOpenDetail: (id: ImportTableDefinition.Id) => void;
  onDelete: (target: TableDefinitionDeleteTarget) => void;
  onForceRemove: (target: TableDefinitionDeleteTarget) => void;
};

/**
 * @feature テーブル定義一覧テーブル
 * @capabilities 表示名・ソースシステム・取込モード・更新日時・最終更新者の一覧表示, 詳細表示, 削除, 強制削除
 * @behavior 行クリック→詳細画面へ遷移, ⋯メニューから「削除」（プロジェクト管理者）/「強制削除（管理者）」（システム管理者）を選択。どちらの権限も無い場合は操作メニュー自体を非表示
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
export function TableDefinitionList({
  rows,
  loading,
  sourceSystemNameById,
  memberNameById,
  isSystemAdmin,
  isProjectAdmin,
  onOpenDetail,
  onDelete,
  onForceRemove,
}: TableDefinitionListProps) {
  // 削除はプロジェクト管理者、強制削除はシステム管理者のみ。どちらも無い場合は操作メニュー自体を出さない。
  const showActionMenu = isProjectAdmin || isSystemAdmin;
  return (
    <List
      loader={
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      }
      loading={loading}
    >
      <ListHead>
        <ListRow>
          <ListHeadCell>表示名</ListHeadCell>
          <ListHeadCell>ソースシステム</ListHeadCell>
          <ListHeadCell width={96}>取込モード</ListHeadCell>
          <ListHeadCell width={140}>更新日時</ListHeadCell>
          <ListHeadCell width={140}>最終更新者</ListHeadCell>
          <ListHeadCell width={56} />
        </ListRow>
      </ListHead>
      <ListBody>
        {rows.map((row) => {
          const d = row.definition;
          const effectiveRevision = row.activeRevision ?? row.lastRevision;
          return (
            <ListRow key={d.id} className="cursor-pointer hover:bg-action-subtle-hover" onClick={() => onOpenDetail(d.id)}>
              <ListCell>
                <span className="block max-w-0 min-w-full truncate text-sm font-medium text-default">{d.displayName}</span>
              </ListCell>
              <ListCell>
                <span className="block max-w-0 min-w-full truncate text-sm text-muted-light">
                  {sourceSystemNameById.get(d.sourceSystemId) ?? d.sourceSystemId}
                </span>
              </ListCell>
              <ListCell width={96}>
                <span className="text-sm">{importTableDefinitionLoadModeLabel(effectiveRevision.loadMode)}</span>
              </ListCell>
              <ListCell width={140}>
                <span className="text-sm text-muted-light">{d.updatedAt.format('YYYY-MM-DD HH:mm')}</span>
              </ListCell>
              <ListCell width={140}>
                <span className="block max-w-0 min-w-full truncate text-sm text-muted-light">
                  {memberNameById.get(d.updatedBy) ?? d.updatedBy}
                </span>
              </ListCell>
              <ListCell width={56}>
                {showActionMenu ? (
                  // biome-ignore lint/a11y/noStaticElementInteractions: stop row click; menu items handle their own a11y
                  <span
                    className="flex justify-end"
                    onClick={(e: MouseEvent<HTMLSpanElement>) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Menu>
                      <MenuTrigger>
                        <IconButton size="sm" as="div" variant="quiet" aria-label="操作メニュー">
                          <span aria-hidden className="text-base leading-none">
                            ⋯
                          </span>
                        </IconButton>
                      </MenuTrigger>
                      <MenuContent align="end" className="w-[200px]">
                        {isProjectAdmin && (
                          <MenuItem color="negative" onSelect={() => onDelete({ id: d.id, displayName: d.displayName })}>
                            削除
                          </MenuItem>
                        )}
                        {isSystemAdmin && (
                          <MenuItem
                            className="admin-ui"
                            color="negative"
                            onSelect={() => onForceRemove({ id: d.id, displayName: d.displayName })}
                          >
                            強制削除（管理者）
                          </MenuItem>
                        )}
                      </MenuContent>
                    </Menu>
                  </span>
                ) : null}
              </ListCell>
            </ListRow>
          );
        })}
      </ListBody>
    </List>
  );
}
